import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import en from '@bfg/lng/i18n.en'
import { debugMode } from '@bfg/common'

/**
 * Английский лежит в бандле, всё остальное приезжает отдельными чанками.
 *
 * Раньше сюда статически импортировались все пятнадцать наборов — 35 КБ в gzip
 * из 193 КБ бандла, при том что посетителю нужен ровно один. Английский из
 * бандла убирать нельзя: он fallbackLng, и при отсутствии ключа в любой локали
 * i18next идёт именно в него.
 *
 * Четыре последних ключа — не языки, а посадочные страницы (тексты английские,
 * набор строк свой). Ленятся так же, как локали.
 */
const LAZY = {
  ar: () => import(/* webpackChunkName: "lng-ar" */ '@bfg/lng/i18n.ar'),
  de: () => import(/* webpackChunkName: "lng-de" */ '@bfg/lng/i18n.de'),
  es: () => import(/* webpackChunkName: "lng-es" */ '@bfg/lng/i18n.es'),
  fr: () => import(/* webpackChunkName: "lng-fr" */ '@bfg/lng/i18n.fr'),
  hi: () => import(/* webpackChunkName: "lng-hi" */ '@bfg/lng/i18n.hi'),
  pl: () => import(/* webpackChunkName: "lng-pl" */ '@bfg/lng/i18n.pl'),
  pt: () => import(/* webpackChunkName: "lng-pt" */ '@bfg/lng/i18n.pt'),
  ru: () => import(/* webpackChunkName: "lng-ru" */ '@bfg/lng/i18n.ru'),
  tr: () => import(/* webpackChunkName: "lng-tr" */ '@bfg/lng/i18n.tr'),
  // Ключ ресурса zh, файл исторически называется i18n.zn — не опечатка.
  zh: () => import(/* webpackChunkName: "lng-zh" */ '@bfg/lng/i18n.zn'),
  'baby-generator':
    () => import(/* webpackChunkName: "lng-baby-generator" */ '@bfg/lng/i18n.baby-generator'),
  'what-will-my-baby-look-like':
    () => import(/* webpackChunkName: "lng-what-will-my" */ '@bfg/lng/i18n.what-will-my-baby-look-like'),
  'what-will-our-baby-look-like':
    () => import(/* webpackChunkName: "lng-what-will-our" */ '@bfg/lng/i18n.what-will-our-baby-look-like'),
  'what-would-our-baby-look-like':
    () => import(/* webpackChunkName: "lng-what-would-our" */ '@bfg/lng/i18n.what-would-our-baby-look-like'),
}

const supportedLanguages = ['en', ...Object.keys(LAZY)]

const initPromise = i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    debug: Boolean(debugMode),
    // Список полный, хотя ресурс пока один: по нему детектор решает, какой
    // язык вообще допустим. Урезать его до загруженных нельзя — тогда /ru/
    // будет определяться как английский.
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false // react already safes from xss
    },
    detection: {
      order: ['querystring', 'path'],
    },
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i']
  })

/**
 * Догружает перевод того языка, который определил детектор.
 *
 * Логика определения намеренно не дублируется: detection отрабатывает внутри
 * init(), и здесь мы просто спрашиваем результат у самого i18next. Любая
 * собственная копия разбора querystring и пути рано или поздно разошлась бы
 * с оригиналом.
 *
 * Вызывать до монтирования React — иначе на не-английской странице мелькнёт
 * английский текст.
 */
export async function loadDetectedLanguage () {
  await initPromise

  // language может прийти с регионом (ru-RU), поэтому проверяем и цепочку
  // фолбэков, которую i18next выстроил сам.
  const candidates = [i18n.language, ...(i18n.languages || [])]
  const lng = candidates.find(code => Object.prototype.hasOwnProperty.call(LAZY, code))
  if (lng === undefined) {
    return
  }

  const module = await LAZY[lng]()
  i18n.addResourceBundle(lng, 'translation', module.default, true, true)
  await i18n.changeLanguage(lng)
}

export default i18n
